import { el } from '../../core/dom.js';
import { formatRelative } from '../../core/format.js';
import { button } from '../../components/button.js';
import { errorPanel } from '../../components/error.js';
import { toast } from '../../components/toast.js';
import { documentMeta, formatBytes, shortCode, writtenByBonnie } from './model.js';
import { DOC_RECENT, SEARCH_WAIT, later, mergeRows } from './paging.js';
import { statusNote } from './status.js';
import { openDocumentViewer } from './viewer.js';

/**
 * @param {{
 *   docs: object[], total: number, status: string, error: string, tab: string,
 *   query: string, search: { rows: object[], total: number } | null,
 *   productId?: string,
 *   onViewAll: () => void, onRefresh: () => void,
 *   onPage: (spec: { q: string, offset: number }) => Promise<{ ok: boolean, error?: string, rows: object[], total: number }>,
 *   onRemember: (q: string, rows: object[] | null, total: number) => void,
 * }} opts
 */
export function renderDocuments(opts) {
  if (opts.status === 'error') {
    return errorPanel({
      title: 'Documents could not be loaded',
      message: opts.error || 'Try again.',
      action: button('Retry', { onClick: opts.onRefresh }),
    });
  }
  const section = el('section', { class: 'kb-rv kb-rv-4' });
  if (opts.tab !== 'documents') {
    const recent = opts.docs.slice(0, DOC_RECENT);
    section.append(
      docHeading(`Recent documents · ${opts.total}`, opts.onViewAll),
      documentNodes(recent, false, opts.productId),
    );
    return section;
  }
  return documentBrowser(section, opts);
}

function documentBrowser(section, opts) {
  let query = opts.query || '';
  let searching = Boolean(query.trim() && opts.search);
  let browseRows = opts.docs.slice();
  let browseTotal = opts.total;
  let searchRows = opts.search ? opts.search.rows.slice() : [];
  let searchTotal = opts.search ? opts.search.total : 0;
  let busy = false;
  let ticket = 0;
  const head = el('div');
  const body = el('div');
  const searchBox = el('input', {
    class: 'kb-search',
    type: 'search',
    placeholder: 'Search documents',
    'aria-label': 'Search documents',
    value: query,
    onInput(event) {
      query = event.currentTarget.value;
      opts.onRemember(query, searching ? searchRows : null, searchTotal);
      runSearch();
    },
  });
  const runSearch = later(() => lookup(query), SEARCH_WAIT);
  section.append(head, searchBox, body);
  paint();
  return section;

  function paint() {
    const shown = searching ? searchTotal : browseTotal;
    head.replaceChildren(docHeading(`Documents · ${shown}`, null));
    paintBody();
  }

  function paintBody() {
    const rows = searching ? searchRows : browseRows;
    const total = searching ? searchTotal : browseTotal;
    const nodes = [documentNodes(rows, searching, opts.productId)];
    if (rows.length && rows.length < total) nodes.push(moreButton());
    body.replaceChildren(...nodes);
  }

  function moreButton() {
    return el('div', { class: 'kb-more' }, el('button', {
      class: 'kb-link',
      type: 'button',
      disabled: busy ? true : null,
      onClick: () => more(),
    }, busy ? 'Loading…' : 'Load more'));
  }

  async function lookup(q) {
    const mine = ++ticket;
    const text = q.trim();
    if (!text) {
      searching = false;
      opts.onRemember('', null, 0);
      paint();
      return;
    }
    const page = await opts.onPage({ q: text, offset: 0 });
    if (mine !== ticket || !section.isConnected) return;
    if (!page.ok) {
      toast(page.error || 'Could not search documents', 'error');
      return;
    }
    searching = true;
    searchRows = page.rows;
    searchTotal = page.total;
    opts.onRemember(text, searchRows, searchTotal);
    paint();
  }

  async function more() {
    if (busy) return;
    busy = true;
    paintBody();
    const page = await opts.onPage({
      q: searching ? query.trim() : '',
      offset: searching ? searchRows.length : browseRows.length,
    });
    busy = false;
    if (!section.isConnected) return;
    if (!page.ok) {
      toast(page.error || 'Could not load documents', 'error');
      paintBody();
      return;
    }
    if (searching) {
      searchRows = mergeRows(searchRows, page.rows);
      searchTotal = page.total;
      opts.onRemember(query.trim(), searchRows, searchTotal);
    } else {
      browseRows = mergeRows(browseRows, page.rows);
      browseTotal = page.total;
    }
    paint();
  }
}

function docHeading(label, onViewAll) {
  return el('div', { class: 'kb-sec-h' },
    el('span', { class: 'kb-kicker' }, label),
    onViewAll
      ? el('button', { class: 'kb-link', type: 'button', onClick: onViewAll }, 'View all ', el('span', { class: 'arw' }, '→'))
      : null);
}

function documentNodes(rows, searching, productId) {
  if (!rows.length) {
    return searching
      ? statusNote('No documents match', 'Try a different search.')
      : statusNote('No documents yet', 'Upload a file, or wait for Bonnie to write one.');
  }
  return el('div', { class: 'kb-docs' }, rows.map((doc) => card(doc, productId)));
}

function ago(iso) {
  const when = iso ? formatRelative(iso) : '';
  if (!when || when === '—') return '';
  if (when === 'now') return 'just now';
  return `${when} ago`;
}

function card(doc, productId) {
  const meta = documentMeta(doc);
  const title = doc.title || meta.original_name || 'Untitled';
  const when = ago(doc.created_at);
  const origin = writtenByBonnie(doc)
    ? `✦ Written by Bonnie${when ? ` · ${when}` : ''}`
    : `Uploaded${when ? ` · ${when}` : ''}`;
  return el('button', {
    type: 'button',
    class: 'kb-glass kb-lift kb-doc',
    'aria-label': `Open ${title}`,
    onClick: () => openDocumentViewer(doc, { productId }),
  },
  el('div', { class: 'kb-doc-meta' },
    el('span', null, shortCode(doc.id, 'DOC')),
    el('span', null, formatBytes(doc.file_size))),
  el('h3', null, title),
  el('div', { class: 'kb-bars', 'aria-hidden': 'true' }, el('span'), el('span'), el('span')),
  el('span', { class: 'kb-origin' }, origin));
}
